import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Ruler, BarChart3, FileSignature, FileSpreadsheet, ClipboardList } from "lucide-react";
import VolumetryTab from "@/components/obra/VolumetryTab";
import GeneratorsTab from "@/components/obra/GeneratorsTab";
import AmendmentsTab from "@/components/obra/AmendmentsTab";
import EstimacionesControlTab from "@/components/obra/EstimacionesControlTab";

export default function ObraControl() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [tab, setTab] = useState("generators");
  const [budgetItems, setBudgetItems] = useState([]);
  const canManage = ["admin", "contador"].includes(user?.role);

  useEffect(() => {
    api.get("/projects").then(({ data }) => {
      setProjects(data);
      if (data.length && !projectId) setProjectId(data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/budget`).then(({ data }) => {
      const list = Array.isArray(data) ? data : data.items || [];
      setBudgetItems(list.filter((b) => b.level === "item"));
    }).catch(() => setBudgetItems([]));
  }, [projectId]);

  const downloadExcel = async () => {
    const res = await api.get(`/projects/${projectId}/control/excel`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "CONTROL_OBRA.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const project = projects.find((p) => p.id === projectId);

  return (
    <div data-testid="obra-control-page">
      <PageHeader subtitle="Generadores, volumetría y estimaciones" title="Control de Obra"
        actions={projectId && (
          <button data-testid="control-excel-btn" onClick={downloadExcel} className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold" style={{ borderColor: "var(--green)", color: "var(--green)" }}>
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
        )} />

      <div className="px-4 lg:px-8 pt-4 flex gap-2 flex-wrap items-center">
        <TabBtn active={tab === "generators"} onClick={() => setTab("generators")} testId="tab-generators"><Ruler className="w-3.5 h-3.5" /> Generadores</TabBtn>
        <TabBtn active={tab === "volumetry"} onClick={() => setTab("volumetry")} testId="tab-volumetry"><BarChart3 className="w-3.5 h-3.5" /> Volumetría</TabBtn>
        <TabBtn active={tab === "amendments"} onClick={() => setTab("amendments")} testId="tab-amendments"><FileSignature className="w-3.5 h-3.5" /> Convenios</TabBtn>
        <TabBtn active={tab === "estimaciones"} onClick={() => setTab("estimaciones")} testId="tab-est-control"><ClipboardList className="w-3.5 h-3.5" /> Estimaciones</TabBtn>
        <div className="ml-auto flex items-center gap-2">
          <span className="label-mono">Obra:</span>
          <select data-testid="control-project-select" value={projectId} onChange={(e) => setProjectId(e.target.value)} className="input-cyber px-3 py-1.5 text-sm max-w-[320px]">
            {projects.map((p) => <option key={p.id} value={p.id} style={{ background: "#0a0f1d" }}>{p.code} · {p.name}</option>)}
          </select>
        </div>
      </div>

      <div className="px-4 lg:px-8 py-6">
        {!projectId && <p className="text-slate-500 text-sm">Sin obras disponibles.</p>}
        {projectId && tab === "generators" && <GeneratorsTab key={projectId} projectId={projectId} project={project} budgetItems={budgetItems} user={user} />}
        {projectId && tab === "volumetry" && <VolumetryTab key={projectId} projectId={projectId} canManage={["admin", "contador", "auxiliar"].includes(user?.role)} />}
        {projectId && tab === "amendments" && <AmendmentsTab key={projectId} projectId={projectId} budgetItems={budgetItems} canManage={canManage} user={user} />}
        {projectId && tab === "estimaciones" && <EstimacionesControlTab key={projectId} projectId={projectId} user={user} />}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"
    style={{ background: active ? "rgba(34,211,238,0.12)" : "transparent", color: active ? "var(--cyan)" : "var(--text-dim)", border: `1px solid ${active ? "var(--cyan)" : "var(--border)"}` }}>
    {children}
  </button>
);
